import { useEffect, useRef } from "react";
import { Box, Paper, Stack } from "@mui/material";
import MessageBubble from "./MessageBubble";
import { useParams } from "react-router-dom";
import { useCrudGetHelper } from "../../../crudHelpers/crudHelper";
import { getThreadConversations, getUserCreditUsagePerConversations } from "../../../crud/ai.crud";
import { usePermissionProviderContext } from "../../../../contexts/contexts";
import TextView from "../../../../theme/partials/Typography";
import { isEmpty } from "../../../../helpers/Functions";

const ThreadSummary = ({
	totalMessages,
	totalTokens,
	summarizeUsage,
	chatsThisMonth,
	tokensThisMonth,
}) => {

	return (
		<Box
			sx={{
				position: 'fixed',
				bottom: { xs: 8, sm: 12, md: 16 },
				left: '50%',
				transform: 'translateX(-50%)',
				width: { xs: '95%', sm: '90%', md: '60%' },
				maxWidth: 1400,
				px: { xs: 0.5, sm: 0 },
				zIndex: (theme) => theme.zIndex.modal - 1,
			}}
		>
			<Paper
				elevation={0}
				sx={{
					p: { xs: 1.5, sm: 2 },
					borderRadius: 4,
					border: '1px solid',
					borderColor: 'grey.300',
					backgroundColor: 'background.paper',
					display: 'flex',
					flexDirection: 'column',
					gap: { xs: 1.5, sm: 2 },
					boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
				}}
			>
				<TextView fontcolor="septenary">
					Thread Summary
				</TextView>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
						<TextView fontcolor="septenary" fontsize="secondary">Total Messages:</TextView>
						<TextView fontcolor="septenary" fontsize="secondary">{totalMessages}</TextView>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
						<TextView fontcolor="septenary" fontsize="secondary">Total Tokens:</TextView>
						<TextView fontcolor="septenary" fontsize="secondary">{totalTokens}</TextView>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
						<TextView fontcolor="septenary" fontsize="secondary">Summarize Usage:</TextView>
						<TextView fontcolor="septenary" fontsize="secondary">{Number.isFinite(summarizeUsage) ? summarizeUsage : '-'}</TextView>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
						<TextView fontcolor="septenary" fontsize="secondary">Chats This Month:</TextView>
						<TextView fontcolor="septenary" fontsize="secondary">{Number.isFinite(chatsThisMonth) ? chatsThisMonth : '-'}</TextView>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
						<TextView fontcolor="septenary" fontsize="secondary">Tokens This Month:</TextView>
						<TextView fontcolor="septenary" fontsize="secondary">{Number.isFinite(tokensThisMonth) ? tokensThisMonth : '-'}</TextView>
					</Box>
				</Box>
			</Paper>
		</Box>
	);
}

const EmptyState = () => {
	return (
		<Box
			sx={{
				p: 2,
				width: '100%',
				height: 'calc(100vh - 160px)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Paper
				elevation={0}
				sx={{
					border: '1px solid',
					borderColor: 'grey.300',
					backgroundColor: 'background.paper',
					borderRadius: 4,
					px: 4,
					py: 6,
					textAlign: 'center',
					maxWidth: 720,
					width: '100%',
					boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
				}}
			>
				<TextView>
					Chat history is empty
				</TextView>
				<TextView>
					There are no messages in this thread yet.
				</TextView>
			</Paper>
		</Box>
	);
}

const ThreadConversationsLoading = () => {
	return (
		<Box
			sx={{
				p: 2,
				width: '100%',
				minHeight: '80vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Stack direction="row" spacing={2} alignItems="center">
				<Box
					sx={{
						animation: 'pulse 1.5s ease-in-out infinite',
						'@keyframes pulse': {
							'0%': { transform: 'scale(1)', opacity: 0.6 },
							'50%': { transform: 'scale(1.05)', opacity: 1 },
							'100%': { transform: 'scale(1)', opacity: 0.6 },
						},
					}}
				>
					<Box
						sx={{
							width: 28,
							height: 28,
							borderRadius: '50%',
							border: '3px solid',
							borderColor: 'grey.400',
							borderTopColor: 'grey.600',
							animation: 'spin 1s linear infinite',
							'@keyframes spin': {
								'0%': { transform: 'rotate(0deg)' },
								'100%': { transform: 'rotate(360deg)' },
							},
						}}
					/>
				</Box>
				<TextView fontcolor="septenary">
					Loading Conversations...
				</TextView>
			</Stack>
		</Box>
	);
}

export default function UserThreadsConversations() {
	const { user_id, thread_id } = useParams();

	const endRef = useRef(null);
	const chatContainerRef = useRef(null);

	const { currentCompanyId } = usePermissionProviderContext();

	const {
		responseData: threadUsageData,
		initCall: initThreadUsageCall,
		isLoading: threadUsageLoading,
	} = useCrudGetHelper(getUserCreditUsagePerConversations);

	const {
		responseData: threadConversationsData,
		initCall: initThreadConversationsCall,
		isLoading: threadConversationsLoading,
		setIsLoading,
	} = useCrudGetHelper(getThreadConversations);

	const threadDetails = threadUsageData?.data?.[0] || {};
	const threadConversations = threadConversationsData?.data || [];

	const totalMessages = threadDetails?.message_count || 0;
	const totalTokens = threadDetails?.total_tokens || 0;
	const summarizeUsage = threadDetails?.summarize_usage || 0;
	const chatsThisMonth = threadDetails?.chat_for_this_month || 0;
	const tokensThisMonth = threadDetails?.tokens_for_this_month || 0;

	useEffect(() => {
		setIsLoading(true);
	}, []);

	useEffect(() => {
		if (!currentCompanyId || !user_id || !thread_id) {
			return;
		}

		initThreadUsageCall({
			company_id: currentCompanyId,
			user_id: user_id,
			thread_id: thread_id,
		});

		initThreadConversationsCall({
			thread_id: thread_id,
		});
	}, [user_id, currentCompanyId, thread_id]);

	if (isEmpty(threadConversations) && !threadUsageLoading && !threadConversationsLoading) {
		return <EmptyState />;
	}

	return (
		<>
			<Box
				ref={chatContainerRef}
				sx={{ height: "100%", width: "100%", overflowY: "auto", display: "flex", flexDirection: "column", paddingInline: 2, scrollbarWidth: "thin" }}
			>
				<Box style={{ width: "100%" }}>
					{threadUsageLoading && (
						<ThreadConversationsLoading />
					)}

					{!threadUsageLoading && (
						<MessageBubble
							messageThread={threadConversations}
							messagesEndRef={endRef}
							isSubmitting={false}
							runStatus={"Idle"}
							showMeta
						/>
					)}
					<div style={{ height: '100px', minHeight: '100px' }} />
				</Box>
			</Box>

			<ThreadSummary
				totalMessages={totalMessages}
				totalTokens={totalTokens}
				summarizeUsage={summarizeUsage}
				chatsThisMonth={chatsThisMonth}
				tokensThisMonth={tokensThisMonth}
			/>
		</>
	);
}
